// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Third party imports
import { DxDataGridComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Constant imports
import { StringFormatConstants } from '../../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../../_constants/messages-intervals';
import { ActionConstants } from '../../../../../_constants/action-constants';

// Service imports
import { ServerErrorService } from '../../../../../_services/shared-services/server-error.service';
import { ARPaymentService } from '../../../_services/account-receivable/ar-payment.service';
import { PrecisionService } from '../../../../../_services/shared-services/precision.service';

// Model imports
import { ARPaymentParameterDto } from '../../../_models/account-receivable/ar-payment/ar-payment-parameter-dto';
import { ARPaymentTransferEditorResultDto } from '../../../_models/account-receivable/ar-payment/ar-payment-transfer-editor-result-dto';
import { ARPaymentTransferRawDto } from '../../../_models/account-receivable/ar-payment/ar-payment-transfer-raw-dto';
import { ARPaymentTransferAccountDetailsRawDto } from '../../../_models/account-receivable/ar-payment/ar-payment-transfer-account-details-raw-dto';

@Component({
    selector: 'app-ar-payment-transfer-editor',
    templateUrl: './ar-payment-transfer-editor.component.html',
    styleUrls: ['./ar-payment-transfer-editor.component.scss']
})
export class ARPaymentTransferEditorComponent implements OnInit {
    @ViewChild('paymentTransferEditorGrid', { static: false }) paymentTransferEditorGrid: DxDataGridComponent;
    @Input() visible = false;
    @Input() arPaymentParam: ARPaymentParameterDto;
    @Input() documentNo: string;
    @Output() onTransferPaymentEditorClose = new EventEmitter<boolean>();

    loadingVisible = false;
    currencyFormat: any;

    addIcon: string = IconConstants.ADD_NEW;
    closeIcon: string = IconConstants.CLOSE;

    paymentTransferResultData: ARPaymentTransferEditorResultDto;
    transferPaymentInfo: ARPaymentTransferRawDto;
    editTransferAccount: ARPaymentTransferAccountDetailsRawDto;

    totalTransfer = 0;

    displayAddTransferAccount = false;
    action: string;
    isForceRefreshRequired = false;
    sourceCommunityId: number;

    constructor(
        private readonly arPaymentService: ARPaymentService,
        private readonly precisionService: PrecisionService,
        private readonly errorService: ServerErrorService
    ) {
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);
    }

    ngOnInit() {
        if (!!this.arPaymentParam) {
            this.populateTransferPaymentEditor();
        }
    }

    onHidden(): void {
        this.closeEditor();
    }

    onAddClick(): void {
        this.action = ActionConstants.ADD;
        this.displayAddTransferAccount = true;
    }

    onAddTransferAccountClose(data: any): void {
        if (!!data) {
            this.transferPaymentInfo.transferAccounts.push(data);
            this.transferPaymentInfo.transferAccounts = this.transferPaymentInfo.transferAccounts.filter(
                (
                    (s) => (a) =>
                        !s.has(a.creditAccountInfo.customerCode) && s.add(a.creditAccountInfo.customerCode)
                )(new Set())
            );
            const totalTransferAmount = this.transferPaymentInfo.transferAccounts
                .map((amount) => amount.transferAmount)
                .reduce((a, s) => a + s);
            this.totalTransfer = this.precisionService.getRoundedNumber(totalTransferAmount);
            this.paymentTransferEditorGrid.instance.refresh();
        }
        this.displayAddTransferAccount = false;
    }

    onEditClick(data: ARPaymentTransferAccountDetailsRawDto): void {
        this.action = ActionConstants.EDIT;
        this.editTransferAccount = data;
        this.displayAddTransferAccount = true;
    }

    onDeleteClick(data: ARPaymentTransferAccountDetailsRawDto): void {
        this.transferPaymentInfo.transferAccounts = this.transferPaymentInfo.transferAccounts.filter((e) => {
            return e.creditAccountInfo.customerCode !== data.creditAccountInfo.customerCode;
        });
    }

    onTransferPaymentClick() {
        this.loadingVisible = true;
        this.transferPaymentInfo.communityId = this.sourceCommunityId;
        this.arPaymentService.updateARPaymentTransfer(this.transferPaymentInfo).subscribe(
            () => {
                notify('Payment Transferred successfully', 'success', MessageInterval.successMsgInterval);
                this.isForceRefreshRequired = true;
                this.closeEditor();
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private populateTransferPaymentEditor(): void {
        this.loadingVisible = true;
        this.arPaymentService.getARPaymentTransferEditor(this.arPaymentParam).subscribe(
            (result: ARPaymentTransferEditorResultDto) => {
                this.paymentTransferResultData = result;
                this.transferPaymentInfo = result.transferPaymentInfo;
                this.sourceCommunityId = result.transferPaymentInfo.communityId; // back up the source community for the web api transfer later
                this.transferPaymentInfo.postDate = new Date();
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
                this.isForceRefreshRequired = false;
                this.closeEditor();
            }
        );
    }

    private closeEditor(): void {
        this.visible = false;
        this.onTransferPaymentEditorClose.emit(this.isForceRefreshRequired);
    }
}
